/**
 * ganttRowHeightSync.ts
 * BUG-2026-0194 — Fixed row height + in-chart vertical scrolling for the
 * Master Gantt.
 *
 * The problem
 * -----------
 * The page used to size the chart from the TOTAL row count (rows × 26 + 100)
 * and let the page scroll. Two complaints followed:
 *   - collapsing a tree node spread the same fixed plot height over FEWER rows,
 *     so rows grew "larger and larger, filling the space";
 *   - the navigator / horizontal scrollbar live at the bottom of the SVG, which
 *     for a tall chart is far below the viewport.
 *
 * Highcharts has `yAxis.staticScale` for constant row heights, but it is
 * silently disabled whenever `chart.height` is set (StaticScale.js
 * onAxisAfterSetOptions), and even with `scrollablePlotArea.minHeight` it only
 * resizes while `chart.scrollablePixelsY === 0` — i.e. never while the plot is
 * actually scrolling. So it cannot combine "constant rows" with "rows scroll
 * inside a viewport-sized chart, navigator pinned at the bottom".
 *
 * The approach
 * ------------
 * The chart is `chart.height = viewport` with `chart.scrollablePlotArea.
 * minHeight` driven by THIS controller on every Highcharts `render`:
 *
 *   rows       = visible treegrid rows (brokenAxis.unitLength honours collapsed
 *                nodes — the same formula Highcharts' own StaticScale uses)
 *   minHeight  = rows × rowPx + chrome      (chrome = x-axis + navigator +
 *                                            scrollbar + margins, measured)
 *   chartHeight = min(viewport, minHeight)  (few rows → chart shrinks to fit)
 *
 * With `scrollablePixelsY > 0` Highcharts pins the x-axis, navigator and
 * scrollbar in a fixed layer and scrolls the plot area natively (mouse wheel
 * works); the treegrid labels scroll with their rows. Rows therefore stay
 * `rowPx` tall no matter how many are expanded — collapsing just makes the
 * scroll region shorter.
 *
 * Re-entrancy: `setSize` redraws synchronously and fires `render` again, so the
 * controller guards itself the way StaticScale guards `redrawTrigger`. A 1 px
 * tolerance prevents ping-pong on sub-pixel chrome differences. `setSize`
 * early-returns when the size is unchanged unless `chart.isDirtyBox` is set —
 * a minHeight-only change must force it.
 *
 * Nothing here calls `chart.update()` — the BUG-2026-0093 leak path stays shut.
 */

import type Highcharts from 'highcharts/highstock';

/** Pixel height of one treegrid row (booking / assignment bar row). */
export const GANTT_ROW_HEIGHT_PX = 26;

/** Below this the "viewport" is unmeasured — fall back to a sane height. */
export const GANTT_MIN_VIEWPORT_PX = 240;

/** Inputs for the pure sizing math (all px except `rows`). */
export interface GanttRowHeightInput {
  /** Visible treegrid rows (collapsed subtrees excluded). */
  rows: number;
  rowPx: number;
  chartHeight: number;
  /** Highcharts plotHeight — INCLUDES scrollablePixelsY when scrolling. */
  plotHeight: number;
  scrollablePixelsY: number;
  /** Space the page gives the chart (chart never grows past this). */
  viewportPx: number;
}

export interface GanttRowHeightTargets {
  rows: number;
  /** `chart.scrollablePlotArea.minHeight` to apply. */
  minHeight: number;
  /** `chart.height` to apply (≤ viewport). */
  chartHeight: number;
}

/**
 * Pure sizing math — unit-testable.
 *   visiblePlot = plotHeight − scrollablePixelsY (the plot that fits on screen)
 *   chrome      = chartHeight − visiblePlot
 */
export function computeGanttRowHeightTargets(input: GanttRowHeightInput): GanttRowHeightTargets {
  const rows = Math.max(1, Math.round(input.rows));
  const visiblePlot = Math.max(0, input.plotHeight - input.scrollablePixelsY);
  const chrome = Math.max(0, input.chartHeight - visiblePlot);
  const minHeight = Math.round(rows * input.rowPx + chrome);
  const viewport = Math.max(GANTT_MIN_VIEWPORT_PX, Math.round(input.viewportPx || 0));
  return { rows, minHeight, chartHeight: Math.min(viewport, minHeight) };
}

/**
 * Visible row count of a treegrid y-axis, or null before the axis has extremes.
 * `brokenAxis.unitLength` is the axis length minus collapsed (broken) ranges —
 * the exact quantity Highcharts' StaticScale multiplies by `staticScale`.
 */
export function visibleTreegridRows(axis: Highcharts.Axis): number | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- brokenAxis / tickInterval are runtime-only fields
  const a = axis as any;
  if (!Number.isFinite(a.min) || !Number.isFinite(a.max)) return null;
  const unit = a.brokenAxis?.unitLength;
  if (typeof unit === 'number' && Number.isFinite(unit)) return unit;
  return a.max + (typeof a.tickInterval === 'number' ? a.tickInterval : 1) - a.min;
}

export interface GanttRowHeightController {
  /** Page-driven: the space available for the chart (px). */
  setViewportHeight(px: number): void;
  /** Recompute; resize when off by more than 1 px. True when a resize ran. */
  apply(chart: Highcharts.Chart): boolean;
  /** Bind as `chart.events.render` (reads `this`). */
  onRender(this: Highcharts.Chart): void;
}

/**
 * Create a controller for one chart instance.
 * @param rowPx       Fixed row height (GANTT_ROW_HEIGHT_PX).
 * @param viewportPx  Initial available height.
 */
export function createGanttRowHeightController(
  rowPx: number,
  viewportPx: number,
): GanttRowHeightController {
  let viewport = viewportPx;
  let adjusting = false;
  let initiated = false;

  const apply = (chart: Highcharts.Chart): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime-only chart fields
    const c = chart as any;
    // Skip re-entrant calls and the throw-away export clone (no scrolling there).
    if (adjusting || !chart || c.renderer?.forExport) return false;
    const yAxis = chart.yAxis?.[0];
    if (!yAxis) return false;
    const rows = visibleTreegridRows(yAxis);
    if (rows === null) return false;

    const targets = computeGanttRowHeightTargets({
      rows,
      rowPx,
      chartHeight: chart.chartHeight,
      plotHeight: chart.plotHeight,
      scrollablePixelsY: typeof c.scrollablePixelsY === 'number' ? c.scrollablePixelsY : 0,
      viewportPx: viewport,
    });

    const chartOptions = chart.options.chart ?? (chart.options.chart = {});
    const scrollable = chartOptions.scrollablePlotArea ?? {};
    const currentMin = typeof scrollable.minHeight === 'number' ? scrollable.minHeight : 0;
    const minDiff = Math.abs(currentMin - targets.minHeight);
    const heightDiff = Math.abs(chart.chartHeight - targets.chartHeight);
    if (minDiff <= 1 && heightDiff <= 1) return false;

    adjusting = true;
    try {
      chartOptions.scrollablePlotArea = { ...scrollable, minHeight: targets.minHeight };
      // setSize() skips everything when the size is unchanged unless the box is
      // dirty — a minHeight-only change still needs afterSetChartSize to run.
      c.isDirtyBox = true;
      // First pass snaps (no visible intermediate layout); later passes animate
      // like Highcharts' own StaticScale does on collapse/expand.
      chart.setSize(undefined, targets.chartHeight, initiated ? undefined : false);
      initiated = true;
    } finally {
      adjusting = false;
    }
    return true;
  };

  return {
    setViewportHeight(px: number): void {
      viewport = px;
    },
    apply,
    onRender(this: Highcharts.Chart): void {
      apply(this);
    },
  };
}
